
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { SmartConsultant } from '../SmartConsultant';
import { LeadFormModal } from '../LeadFormModal';
import { EstateCaseStudies } from '../EstateCaseStudies';
import {
    Calculator, Check, X, Loader2, MessageSquare, Database, Server, ShieldCheck, Lock, Minus, Plus, Cpu, ArrowRight, Home, Building, PieChart, PenTool, Settings, Key, Eye, Activity, Image
} from 'lucide-react';
import { Page } from '../../types';

interface EstatePageProps {
    onNavigate?: (page: Page) => void;
}

const PortfolioOverview = () => {
    const { t } = useTranslation();
    return (
        <div className="w-full h-[400px] bg-[#0F172A] rounded-xl border border-teal-500/20 relative overflow-hidden flex flex-col p-6 shadow-2xl">
            <div className="flex justify-between items-center mb-8 border-b border-teal-500/20 pb-4">
                <span className="text-xs font-mono text-teal-400">{t('estate.portfolio.title')}</span>
                <div className="flex gap-2"><div className="w-2 h-2 rounded-full bg-teal-500"></div></div>
            </div>
            <div className="flex-1 grid grid-cols-2 gap-4">
                <div className="bg-teal-900/10 border border-teal-500/30 rounded p-4 flex flex-col justify-between">
                    <Home size={24} className="text-teal-500 mb-2" />
                    <div>
                        <div className="text-2xl font-bold text-white">142</div>
                        <div className="text-[10px] text-slate-400 uppercase">{t('estate.portfolio.assets')}</div>
                    </div>
                </div>
                <div className="bg-teal-900/10 border border-teal-500/30 rounded p-4 flex flex-col justify-between">
                    <Activity size={24} className="text-teal-500 mb-2" />
                    <div>
                        <div className="text-2xl font-bold text-white">98%</div>
                        <div className="text-[10px] text-slate-400 uppercase">{t('estate.portfolio.occupancy')}</div>
                    </div>
                </div>
                <div className="col-span-2 bg-teal-900/10 border border-teal-500/30 rounded p-4 relative overflow-hidden">
                    <div className="absolute bottom-0 left-0 right-0 h-16 flex items-end px-2 gap-1">
                        {[40, 60, 45, 70, 65, 80, 50, 75, 90, 85].map((h, i) => (
                            <div key={i} className="flex-1 bg-teal-500/40 rounded-t" style={{ height: `${h}%` }}></div>
                        ))}
                    </div>
                    <div className="text-[10px] text-slate-400 uppercase relative z-10">{t('estate.portfolio.trend')}</div>
                </div>
            </div>
        </div>
    );
};

export const EstatePage: React.FC<EstatePageProps> = ({ onNavigate }) => {
    const { t } = useTranslation();
    const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const subSectors = [
        { title: t('estate.sectors.items.01.title'), id: "01", desc: t('estate.sectors.items.01.desc'), highlight: t('estate.sectors.items.01.highlight') },
        { title: t('estate.sectors.items.02.title'), id: "02", desc: t('estate.sectors.items.02.desc'), highlight: t('estate.sectors.items.02.highlight') },
        { title: t('estate.sectors.items.03.title'), id: "03", desc: t('estate.sectors.items.03.desc'), highlight: t('estate.sectors.items.03.highlight') },
        { title: t('estate.sectors.items.04.title'), id: "04", desc: t('estate.sectors.items.04.desc'), highlight: t('estate.sectors.items.04.highlight') },
        { title: t('estate.sectors.items.05.title'), id: "05", desc: t('estate.sectors.items.05.desc'), highlight: t('estate.sectors.items.05.highlight') },
        { title: t('estate.sectors.items.06.title'), id: "06", desc: t('estate.sectors.items.06.desc'), highlight: t('estate.sectors.items.06.highlight') },
        { title: t('estate.sectors.items.07.title'), id: "07", desc: t('estate.sectors.items.07.desc'), highlight: t('estate.sectors.items.07.highlight') },
        { title: t('estate.sectors.items.08.title'), id: "08", desc: t('estate.sectors.items.08.desc'), highlight: t('estate.sectors.items.08.highlight'), variant: 'dark' }
    ];

    const handleOpenModal = () => setIsModalOpen(true);

    return (
        <div className="bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen pt-24 font-sans text-gray-900">
            <section className="relative pt-20 pb-20 px-6 overflow-hidden bg-transparent">
                <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-teal-50 rounded-full blur-[120px] pointer-events-none translate-x-1/3 -translate-y-1/3"></div>
                <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start relative z-10">
                    <div>
                        <Reveal>
                            <div className="inline-block px-4 py-2 bg-teal-50 text-teal-700 text-xs font-bold uppercase tracking-[0.2em] mb-8 border border-teal-100">{t('estate.hero.tagline')}</div>
                            <h1 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-[0.95] mb-8">
                                {t('estate.hero.title_line1')} <br />
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-500">{t('estate.hero.title_line2')}</span>
                            </h1>
                            <p className="text-xl text-gray-500 leading-relaxed mb-10 max-w-lg">{t('estate.hero.subtitle')}</p>
                            <div className="flex flex-wrap gap-4 mb-12">
                                <button onClick={handleOpenModal} className="bg-teal-600 text-white px-6 py-4 font-bold uppercase tracking-widest text-xs hover:bg-teal-700 transition-all shadow-lg rounded-sm flex items-center gap-3">{t('estate.hero.cta_strategy')}</button>
                                <button onClick={() => onNavigate && onNavigate('roi')} className="bg-transparent text-gray-900 border border-gray-200 px-6 py-4 font-bold uppercase tracking-widest text-xs hover:border-teal-600 transition-all rounded-sm flex items-center gap-2"><Calculator size={16} /> {t('estate.hero.cta_roi')}</button>
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.2}><PortfolioOverview /></Reveal>
                </div>
            </section>

            <section className="py-32 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <Reveal>
                        <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
                            <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                                {t('estate.sectors.title').split(' ')[0]}<br />{t('estate.sectors.title').split(' ')[1]}
                            </h2>
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 border-t border-l border-gray-200">
                        {subSectors.map((sector, idx) => (
                            <Reveal key={idx} delay={idx * 0.05}>
                                <div
                                    onClick={handleOpenModal}
                                    className={`group p-12 border-b border-r border-gray-200 transition-all duration-500 min-h-[320px] flex flex-col justify-between relative overflow-hidden h-full cursor-pointer ${sector.variant === 'dark' ? 'bg-[#0B1121] text-white border-gray-800' : 'hover:bg-teal-50/30'}`}
                                >
                                    <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500"><span className={`${sector.variant === 'dark' ? 'text-clever-teal' : 'text-teal-600'} text-xs font-bold uppercase tracking-widest`}>{sector.highlight}</span></div>
                                    <div className={`text-xs font-mono mb-8 ${sector.variant === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>// {sector.id}</div>
                                    <div>
                                        <h3 className={`text-3xl font-bold mb-6 group-hover:translate-x-2 transition-transform ${sector.variant === 'dark' ? 'text-white' : 'text-gray-900'}`}>{sector.title}</h3>
                                        <p className={`leading-relaxed text-lg ${sector.variant === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{sector.desc}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            <EstateCaseStudies />

            <LeadFormModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                sector="Real Estate"
            />
        </div>
    );
};
